// AGC: Admin Panel Layout with Left Sidebar Menu
// AGC: Desktop-style layout for administrative functions
// AGC: Includes nested menu structure with Configurations section and Models section

import React, { useState, useEffect } from 'react'
import { APP_VERSION } from '@droidfarm/shared'
import { useAuth, useTranslation } from '../../hooks'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { getBackendUrl } from '../../utils/backendUrl'
import { useTheme } from '../../context/ThemeContext'
import { AdminThemeToggle } from './ui/AdminThemeToggle'
import { AdminBadge } from './ui/AdminBadge'

// AGC: Import admin pages
import { PatternMatchingAdmin } from './PatternMatchingAdmin'
import { UserManagement } from './UserManagement'
import { TranslationManager } from './TranslationManager'
import { SubscriptionAdmin } from './SubscriptionAdmin'
import { TierConfigAdmin } from './TierConfigAdmin'
import { VoiceCommandsAdmin } from './VoiceCommandsAdmin'
import { StorageRulesAdmin } from './StorageRulesAdmin'
import { BackendLogsAdmin } from './BackendLogsAdmin'
import { DevToolsAdmin } from './DevToolsAdmin'
import { PermissionManagement } from './PermissionManagement'
import { CouponAdmin } from './CouponAdmin'
import { SorteioAdmin } from './SorteioAdmin'
import { WaitlistAdmin } from './WaitlistAdmin'
import { TaskRecommendationsAdmin } from './TaskRecommendationsAdmin'
import { ConabPriceTable } from './ConabPriceTable'
// AGC: Import Models configuration pages
import { MeteorologiaConfig, RecomendacoesConfig, InputsMasterConfig, TaskTemplatesConfig } from './models'
// AGC: Voice ML Admin Dashboard
import { VoiceMLAdmin } from './VoiceMLAdmin'
// AGC: Analytics Dashboard
import { AnalyticsDashboard } from './analytics/AnalyticsDashboard'
// AGC: VoiceConfigAdmin removed - voice settings now integrated into TierConfigAdmin

// AGC: Menu item type with optional children for nested menus
interface MenuItem {
  id: string
  label: string
  icon: string
  component?: React.ReactNode
  children?: MenuItem[]
}

/**
 * AGC: AdminLayout - Main admin panel container
 * AGC: Features:
 * AGC: - Left sidebar with nested menu items
 * AGC: - Expandable "Configurations" section with Tiers and Voice
 * AGC: - Main content area for selected page
 * AGC: - Responsive design
 */
export const AdminLayout: React.FC = () => {
  // AGC: Get current user and logout function
  const { user, logout } = useAuth()
  
  // AGC: Get translation function and formatDate
  const { t, formatDate } = useTranslation()
  const { isDark } = useTheme()

  // AGC: Active menu item
  const [activeMenu, setActiveMenu] = useState<string>('users')
  
  // AGC: Expanded menu sections (models, configurations, and voice expanded by default)
  const [expandedSections, setExpandedSections] = useState<string[]>(['models', 'configurations', 'voice'])
  
  // AGC: Sidebar collapsed state (for mobile)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  
  // AGC: Backend version state
  const [backendVersion, setBackendVersion] = useState<string>('...')
  
  // AGC: Fetch backend version on mount
  useEffect(() => {
    const fetchBackendVersion = async () => {
      try {
        const backendUrl = getBackendUrl()
        // AGC: Try health endpoint first (no auth required)
        const healthResponse = await fetch(`${backendUrl}/health`)
        if (healthResponse.ok) {
          const healthData = await healthResponse.json()
          setBackendVersion(healthData.version || 'unknown')
        } else {
          setBackendVersion('unknown')
        }
      } catch (error) {
        console.error('AGC: Failed to fetch backend version:', error)
        setBackendVersion('unknown')
      }
    }
    
    fetchBackendVersion()
  }, [])

  // AGC: Handle logout
  const handleLogout = async () => {
    await logout()
    window.location.href = '/'
  }

  // AGC: Toggle section expansion
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    )
  }

  // AGC: Menu items configuration with nested structure
  const menuItems: MenuItem[] = [
    {
      id: 'analytics',
      label: t('admin.menu.analytics'),
      icon: '📈',
      component: <AnalyticsDashboard />,
    },
    {
      id: 'users',
      label: t('admin.users'),
      icon: '👥',
      component: <UserManagement />,
    },
    {
      id: 'subscriptions',
      label: t('admin.subscriptions') || 'Assinaturas',
      icon: '💳',
      component: <SubscriptionAdmin />,
    },
    {
      id: 'models',
      label: t('admin.models.title') || 'Modelos',
      icon: '🧠',
      children: [
        {
          id: 'meteorologia',
          label: t('admin.models.meteorologia') || 'Meteorologia',
          icon: '🌦️',
          component: <MeteorologiaConfig />,
        },
        {
          id: 'recomendacoes',
          label: t('admin.models.recomendacoes') || 'Recomendações',
          icon: '🌱',
          component: <RecomendacoesConfig />,
        },
        {
          id: 'inputs',
          label: t('admin.models.inputs') || 'Insumos',
          icon: '📦',
          component: <InputsMasterConfig />,
        },
        {
          id: 'taskTemplates',
          label: t('admin.models.taskTemplates') || 'Tarefas',
          icon: '📋',
          component: <TaskTemplatesConfig />,
        },
      ],
    },
    {
      id: 'configurations',
      label: t('admin.configurations') || 'Configurações',
      icon: '⚙️',
      children: [
        {
          id: 'tierConfig',
          label: t('admin.tierConfig') || 'Planos e Recursos',
          icon: '👑',
          component: <TierConfigAdmin />,
        },
        {
          id: 'permissions',
          label: t('admin.permissions') || 'Permissões',
          icon: '🔐',
          component: <PermissionManagement />,
        },
        {
          id: 'coupons',
          label: t('admin.coupons.title') || 'Cupons',
          icon: '🎟️',
          component: <CouponAdmin />,
        },
        {
          id: 'sorteios',
          label: t('sorteio.admin.title') || 'Sorteios',
          icon: '🎰',
          component: <SorteioAdmin />,
        },
        {
          id: 'waitlist',
          label: t('admin.waitlist') || 'Lista de Espera',
          icon: '📋',
          component: <WaitlistAdmin />,
        },
      ],
    },
    {
      id: 'voice',
      label: t('admin.voice') || 'Voz',
      icon: '🎤',
      children: [
        {
          id: 'patterns',
          label: t('admin.patterns') || 'Padrões de Voz',
          icon: '📝',
          component: <PatternMatchingAdmin />,
        },
        {
          id: 'voiceCommands',
          label: t('admin.voiceValues') || 'Valores de Voz',
          icon: '🎙️',
          component: <VoiceCommandsAdmin />,
        },
        {
          id: 'taskRecommendations',
          label: t('admin.taskRecommendations') || 'Recomendações',
          icon: '⭐',
          component: <TaskRecommendationsAdmin />,
        },
        {
          id: 'voiceML',
          label: t('admin.voiceML') || 'Voice ML',
          icon: '🧠',
          component: <VoiceMLAdmin />,
        },
        {
          id: 'storageRules',
          label: t('admin.storageRules') || 'Regras de Armazenamento',
          icon: '🛢️',
          component: <StorageRulesAdmin />,
        },
      ],
    },
    {
      id: 'translations',
      label: t('admin.translations'),
      icon: '🌐',
      component: <TranslationManager />,
    },
    {
      id: 'backendLogs',
      label: 'Backend Logs',
      icon: '🖥️',
      component: <BackendLogsAdmin />,
    },
    {
      id: 'conab',
      label: t('conab.title') || 'CONAB Preços',
      icon: '📊',
      component: <ConabPriceTable />,
    },
    {
      id: 'devTools',
      label: t('admin.devTools.title') || 'Dev Tools',
      icon: '🛠️',
      component: <DevToolsAdmin />,
    },
  ]

  // AGC: Find active component (including in children)
  const findComponent = (items: MenuItem[], targetId: string): React.ReactNode | null => {
    for (const item of items) {
      if (item.id === targetId && item.component) {
        return item.component
      }
      if (item.children) {
        const found = findComponent(item.children, targetId)
        if (found) return found
      }
    }
    return null
  }

  // AGC: Find active menu item label (including in children)
  const findMenuItem = (items: MenuItem[], targetId: string): MenuItem | null => {
    for (const item of items) {
      if (item.id === targetId) {
        return item
      }
      if (item.children) {
        const found = findMenuItem(item.children, targetId)
        if (found) return found
      }
    }
    return null
  }

  const activeComponent = findComponent(menuItems, activeMenu)
  const activeItem = findMenuItem(menuItems, activeMenu)

  // AGC: Render menu item (handles both regular and nested items)
  const renderMenuItem = (item: MenuItem, isChild: boolean = false) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedSections.includes(item.id)
    const isActive = activeMenu === item.id ||
      (hasChildren && item.children?.some(child => activeMenu === child.id))

    if (hasChildren) {
      return (
        <div key={item.id}>
          <button
            onClick={() => toggleSection(item.id)}
            className={`
              w-full px-3 py-2.5 flex items-center justify-between rounded-lg transition-colors
              ${isActive
                ? 'text-white bg-white/10'
                : 'text-white/65 dark:text-white/55 hover:text-white hover:bg-white/[0.08] dark:hover:bg-white/[0.06]'
              }
            `.replace(/\s+/g, ' ').trim()}
            title={sidebarCollapsed ? item.label : undefined}
          >
            <div className="flex items-center gap-2.5">
              <span className="text-lg">{item.icon}</span>
              {!sidebarCollapsed && (
                <span className="text-sm font-medium">{item.label}</span>
              )}
            </div>
            {!sidebarCollapsed && (
              isExpanded
                ? <ChevronDown className="w-4 h-4 opacity-50" />
                : <ChevronRight className="w-4 h-4 opacity-50" />
            )}
          </button>

          {isExpanded && !sidebarCollapsed && (
            <div className="mt-0.5 space-y-0.5">
              {item.children?.map(child => renderMenuItem(child, true))}
            </div>
          )}
        </div>
      )
    }

    return (
      <button
        key={item.id}
        onClick={() => setActiveMenu(item.id)}
        className={`
          w-full px-3 py-2.5 flex items-center gap-2.5 rounded-lg transition-colors
          ${isChild ? 'pl-8 text-xs' : 'text-sm'}
          ${activeMenu === item.id
            ? 'text-white bg-white/[0.18] dark:bg-white/[0.12]'
            : `${isChild ? 'text-white/50 dark:text-white/45' : 'text-white/65 dark:text-white/55'} hover:text-white hover:bg-white/[0.08] dark:hover:bg-white/[0.06]`
          }
        `.replace(/\s+/g, ' ').trim()}
        title={sidebarCollapsed ? item.label : undefined}
      >
        <span className={isChild ? 'text-base' : 'text-lg'}>{item.icon}</span>
        {!sidebarCollapsed && (
          <span className="font-medium truncate">{item.label}</span>
        )}
        {activeMenu === item.id && !sidebarCollapsed && (
          <span
            className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: '#FFDF00', boxShadow: '0 0 6px rgba(255,223,0,0.5)' }}
          />
        )}
      </button>
    )
  }

  return (
    <div className="min-h-screen bg-admin-bg dark:bg-admin-bg-dark flex transition-colors">
      {/* AGC: Left Sidebar */}
      <aside
        className={`
          text-white transition-all duration-300 flex flex-col sticky top-0 h-screen
          ${sidebarCollapsed ? 'w-16' : 'w-[220px]'}
        `.replace(/\s+/g, ' ').trim()}
        style={{ backgroundColor: isDark ? '#0f172a' : '#065F46' }}
      >
        {/* AGC: Header with logo */}
        <div className="px-3 pt-4 pb-3 border-b border-white/10 flex items-center justify-between">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2.5">
              <span className="text-xl">🌾</span>
              <span className="font-bold text-[15px]">DroidFarm</span>
            </div>
          )}
          {sidebarCollapsed && <span className="text-xl mx-auto">🌾</span>}

          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/70 hover:text-white"
            title={sidebarCollapsed ? t('admin.expand') : t('admin.collapse')}
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4 rotate-90" />}
          </button>
        </div>

        {/* AGC: Admin badge */}
        <div className={`mx-3 mt-3 mb-2 px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-white/85 text-xs font-medium ${sidebarCollapsed ? 'text-center mx-2' : ''}`}>
          {sidebarCollapsed ? '🔧' : `🔧 ${t('admin.title')}`}
        </div>

        {/* AGC: Navigation Menu */}
        <nav className="flex-1 py-2 px-2 overflow-y-auto space-y-0.5">
          {menuItems.map(item => renderMenuItem(item))}
        </nav>

        {/* AGC: Footer */}
        <div className={`px-3 py-3 border-t border-white/10 text-white/40 text-xs ${sidebarCollapsed ? 'text-center' : ''}`}>
          {sidebarCollapsed ? `v${APP_VERSION}` : `${t('common.version')} ${APP_VERSION}`}
        </div>
      </aside>

      {/* AGC: Main Content Area */}
      <main className="flex-1 overflow-auto">
        {/* AGC: Top bar */}
        <header className="sticky top-0 z-10 bg-admin-bg dark:bg-admin-bg-dark px-6 py-3 flex items-center justify-between transition-colors">
          <h1 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            {activeItem?.icon}{' '}
            {activeItem?.label}
          </h1>

          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {formatDate(new Date())}
            </span>

            <span className="px-2 py-1 rounded text-[10px] font-mono font-semibold bg-brand-primary/[0.08] text-brand-primary dark:bg-brand-light/10 dark:text-brand-light">
              Backend v{backendVersion}
            </span>

            {user && (
              <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                <span>{user.email}</span>
                <AdminBadge variant="admin">Admin</AdminBadge>
              </div>
            )}

            <a
              href="/"
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white dark:bg-admin-card-dark border border-slate-200 dark:border-white/5 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
            >
              ← {t('admin.backToApp')}
            </a>

            <AdminThemeToggle />

            <button
              onClick={handleLogout}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-primary dark:bg-brand-light text-white dark:text-[#052e22] hover:opacity-90 transition-colors"
            >
              🚪 {t('common.logout')}
            </button>
          </div>
        </header>

        {/* AGC: Page content */}
        <div className="p-6">
          {activeComponent}
        </div>
      </main>
    </div>
  )
}

/**
 * AGC: Coming Soon placeholder component
 */
const ComingSoon: React.FC<{ title: string }> = ({ title }) => {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center justify-center h-96 text-slate-500 dark:text-slate-400">
      <span className="text-6xl mb-4">🚧</span>
      <h2 className="text-2xl font-semibold text-slate-700 dark:text-slate-300 mb-2">{title}</h2>
      <p>{t('admin.comingSoon')}</p>
    </div>
  )
}

export default AdminLayout
